import React from 'react';
import { useTranslation } from 'react-i18next';

export const RiskDisclosure: React.FC = () => {
    const { t } = useTranslation();

    const risks = t('riskDisclosure.risks', { returnObjects: true }) as Array<{ title: string; content: string }>;
    const notes = t('riskDisclosure.importantNotes', { returnObjects: true }) as Array<string>;

    return (
        <div className="min-h-screen relative overflow-hidden">
            {/* Animated Background Elements */}
            <div className="fixed inset-0 pointer-events-none">
                <div className="floating-element"></div>
                <div className="floating-element"></div>
                <div className="floating-element"></div>
            </div>

            {/* Hero Section */}
            <section className="relative min-h-[40vh] flex items-center justify-center overflow-hidden">
                <div className="hero-background absolute inset-0">
                    <div
                        className="absolute inset-0 opacity-30"
                        style={{
                            backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' xmlns='http://www.w3.org/2000/svg'%3E%3Cdefs%3E%3Cpattern id='grid' width='100' height='100' patternUnits='userSpaceOnUse'%3E%3Cpath d='M 100 0 L 0 0 0 100' fill='none' stroke='rgba(212, 175, 55, 0.1)' stroke-width='1'/%3E%3C/pattern%3E%3C/defs%3E%3Crect width='100%25' height='100%25' fill='url(%23grid)' /%3E%3C/svg%3E")`,
                        }}
                    />
                </div>

                <div className="relative z-10 text-center px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto pt-20">
                    <div className="hero-badge inline-block px-4 py-2 bg-red-500/20 rounded-full mb-6">
                        <span className="text-red-400 font-semibold text-sm uppercase tracking-wider">Risk Notice</span>
                    </div>
                    <h1 className="text-4xl md:text-5xl font-black text-white mb-6 leading-tight bg-gradient-to-r from-red-400 to-orange-400 bg-clip-text text-transparent">
                        {t('riskDisclosure.title')}
                    </h1>
                </div>
            </section>

            {/* Content Section */}
            <section className="section relative -mt-10">
                <div className="container max-w-4xl mx-auto">
                    <div className="bg-surface/80 backdrop-blur-md rounded-2xl border border-red-500/20 p-8 md:p-12 shadow-xl shadow-red-500/5">
                        <p className="text-lg text-gray-300 mb-10 leading-relaxed bg-red-500/5 p-6 rounded-xl border border-red-500/10">
                            <i className="fas fa-exclamation-triangle text-red-500 mr-3"></i>
                            {t('riskDisclosure.description')}
                        </p>

                        <div className="space-y-6 mb-12">
                            {risks.map((risk, index) => (
                                <div key={index} className="modern-card border-red-500/10 hover:border-red-500/30 transition-colors">
                                    <h3 className="text-xl font-bold text-white mb-3 flex items-center gap-3">
                                        <span className="w-8 h-8 rounded-full bg-red-500/10 flex items-center justify-center text-red-400 text-sm">
                                            {index + 1}
                                        </span>
                                        {risk.title.replace(/^\d+\.\s*/, '')}
                                    </h3>
                                    <p className="text-gray-400 leading-relaxed pl-11">
                                        {risk.content}
                                    </p>
                                </div>
                            ))}
                        </div>

                        <div className="mt-10 p-8 bg-gradient-to-br from-red-900/40 to-black border border-red-500/30 rounded-xl relative overflow-hidden">
                            <div className="absolute top-0 left-0 w-1 h-full bg-red-500"></div>
                            <h3 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
                                <i className="fas fa-info-circle text-red-500"></i>
                                {t('riskDisclosure.importantTitle')}
                            </h3>
                            <ul className="space-y-4">
                                {notes.map((note, index) => (
                                    <li key={index} className="flex items-start gap-4 text-gray-300">
                                        <i className="fas fa-circle text-[8px] mt-2.5 text-red-500 flex-shrink-0"></i>
                                        <span className="leading-relaxed">{note}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};
